import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Button from 'components/Button';
import styles from './Expenses.module.scss';
import { Expense as ExpenseType } from 'types/expenses';

interface VoidExpensesModalProps {
  show: boolean;
  onHide: () => void;
  expense: ExpenseType | null;
  onConfirm: (reason: string) => void;
  isPending: boolean;
}

function VoidExpensesModal({
  show,
  onHide,
  expense,
  onConfirm,
  isPending,
}: VoidExpensesModalProps) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (show) {
      setReason('');
    }
  }, [show]);

  if (!expense) {
    return null;
  }

  const handleConfirm = () => {
    onConfirm(reason.trim());
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      className={styles.modal}
      contentClassName={styles.modalContent}
      dialogClassName={styles.modalDialog}
      backdrop="static"
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>Void Expense</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <div className={styles.rollbackDetails}>
          <div className={styles.detailRow}>
            <strong>Description:</strong>
            <span>{expense.description || 'No description'}</span>
          </div>
          <div className={styles.detailRow}>
            <strong>Date:</strong>
            <span>{formatDate(expense.date)}</span>
          </div>
          <div className={styles.detailRow}>
            <strong>Total Expense:</strong>
            <span>${formatCurrency(expense.totalExpense)}</span>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="voidReason" className={styles.label}>
            Reason for Voiding
          </label>
          <textarea
            id="voidReason"
            placeholder="Enter the reason for voiding this expense..."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className={styles.textareaInput}
            rows={4}
          />
        </div>
      </Modal.Body>
      <Modal.Footer className={styles.modalFooter}>
        <Button
          type="button"
          variant="outline"
          onClick={onHide}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button type="button" onClick={handleConfirm} disabled={isPending}>
          {isPending ? 'Voiding...' : 'Void Expense'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default VoidExpensesModal;
